<template>
    <div>
        <v-card flat>
            <v-card-title>
                Customers
                
                <v-spacer></v-spacer>

                <v-btn depressed color="green" class="white--text" @click="openEditDialog(null)">Create Customer</v-btn>
            </v-card-title>
            <v-card-subtitle>Add or edit customers</v-card-subtitle>

            <v-card-text class="mt-4">
                <v-row>
                    <v-col cols="12" md="4" offset-md="8">
                        <v-text-field v-model="filter" placeholder="Filter" append-icon="mdi-magnify"></v-text-field>
                    </v-col>
                </v-row>
                <v-data-table :headers="headers" :items="customers" :search="filter" :loading="loading" :items-per-page="15">
                    <template v-slot:item.nightRate="{ item }">{{ formatMoney( item.nightRate )}}</template>
                    <template v-slot:item.tax="{ item }">{{ item.tax }}%</template>
                    <template v-slot:item.parkingFee="{ item }">{{ formatMoney( item.parkingFee )}}</template>
                    <template v-slot:item.serviceFee="{ item }">{{ item.serviceFee }}%</template>
                    <template v-slot:item.dailyTotal="{ item }">{{ formatMoney( item.dailyTotal )}}</template>
                    <template v-slot:item.frequency="{ item }">{{ item.frequency == null ? 'N/A' : item.frequency + ' days' }}</template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn small depressed color="blue-grey darken-3" class="mr-2 white--text" @click="openEditDialog(item)">
                            <v-icon small left>mdi-pencil</v-icon>
                            Edit
                        </v-btn>

                        <v-btn small depressed color="success" class="mr-2 white--text" @click="openPointsDialog(item)">
                            <v-icon small left>mdi-eye</v-icon>
                            Points
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="customerDialog" persistent scrollable fullscreen width="900">
            <v-card>
                <v-card-title>
                    {{ dialogHeader }}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="customerDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="6">
                            <v-subheader class="pl-0">General Information</v-subheader>
                            <v-row>
                                <v-col cols="6">
                                    <v-text-field outlined v-model="firstName" dense label="First Name" :error-messages="firstNameError"></v-text-field>
                                </v-col>

                                <v-col cols="6">
                                    <v-text-field outlined v-model="lastName" dense label="Last Name" :error-messages="lastNameError"></v-text-field>
                                </v-col>

                                <v-col cols="12">
                                    <v-text-field outlined v-model="phone" type="tel" dense label="Phone" v-mask="'(###) ###-####'" :error-messages="phoneError"></v-text-field>
                                </v-col>

                                <v-col cols="12">
                                    <v-text-field outlined v-model="email" type="email" dense label="Email" :error-messages="emailError"></v-text-field>
                                </v-col>
                            </v-row>

                            <v-subheader class="pl-0">Address</v-subheader>

                            <v-row>
                                <v-col cols="12">
                                    <v-text-field outlined v-model="address1" dense label="Line 1" :error-messages="address1Error"></v-text-field>
                                </v-col>

                                <v-col cols="12">
                                    <v-text-field outlined v-model="address2" dense label="Line 2" :error-messages="address2Error"></v-text-field>
                                </v-col>
                                
                                <v-col cols="4">
                                    <v-text-field outlined v-model="city" dense label="City" :error-messages="cityError"></v-text-field>
                                </v-col>

                                <v-col cols="4">
                                    <v-text-field outlined v-model="state" dense label="State" v-mask="'AA'" :error-messages="stateError"></v-text-field>
                                </v-col>

                                <v-col cols="4">
                                    <v-text-field outlined v-model="zipcode" dense label="Zip code" v-mask="['#####','#####-####']" :error-messages="zipcodeError"></v-text-field>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="6">
                                    <v-subheader class="pl-0">Frequency</v-subheader>
                                    <v-text-field outlined v-model="frequencyFrom" dense label="Start Frequency From" :error-messages="frequencyFromError"></v-text-field>
                                    <v-text-field outlined v-model="frequency" dense label="Frequency (number of days)" :error-messages="frequencyError"></v-text-field>
                                    <v-checkbox outlined v-model="nextDay" dense label="Generate invoice on next day" class="ma-0"></v-checkbox>
                                </v-col>

                                <v-col cols="6">
                                    <v-subheader class="pl-0">Financial Information</v-subheader>
                                    <v-text-field outlined v-model="cardType" dense label="Card Type" :error-messages="cardTypeError"></v-text-field>
                                    <v-text-field outlined v-model="cardLast4" dense label="Card Last 4" :error-messages="cardLast4Error"></v-text-field>
                                </v-col>
                            </v-row>
                        </v-col>

                        <v-col cols="6">
                            <v-subheader class="pl-0">
                                Rates

                                <v-spacer></v-spacer>
                                <v-btn depressed small color="primary" @click="openRateDialog()"><v-icon left>mdi-plus</v-icon> new</v-btn>
                            </v-subheader>
                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Night Rate</th>
                                        <th>Tax (%)</th>
                                        <th>Parking Fee</th>
                                        <th>Service Fee (%)</th>
                                        <th>Daily Total</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(rate, ix) in rates">
                                        <tr :key="ix">
                                            <td>{{ rate.from_date }}</td>
                                            <td>{{ rate.to_date }}</td>
                                            <td>{{ formatMoney( rate.night_rate ) }}</td>
                                            <td>{{ rate.tax }}%</td>
                                            <td>{{ formatMoney( rate.parking_fee ) }}</td>
                                            <td>{{ rate.service_fee }}%</td>
                                            <td>{{ formatMoney( rate.daily_total ) }}</td>
                                            <td><v-icon @click="openRateDialog(rate)">mdi-pencil</v-icon></td>
                                        </tr>
                                    </template>
                                </tbody>
                            </v-simple-table>
                            <v-alert v-show="ratesError.length > 0" color="error" dense text border="left">{{ ratesError }}</v-alert>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn depressed text @click="customerDialog = false">Cancel</v-btn>
                    <v-btn depressed color="primary" @click="saveCustomer" :loading="saving">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rateDialog" width="800">
            <v-card>
                <v-card-title>Add/Edit customer rate</v-card-title>
                <v-card-text>
                    <v-alert v-model="rateAlert" :color="rateAlertColor" border="left" text dense>{{ rateAlertMessage }}</v-alert>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-menu ref="fromDateMenu" v-model="fromDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                <template v-slot:activator="{ on }">
                                    <v-text-field v-model="fromDateFormatted" label="From Date" outlined dense hint="MM/DD/YYYY" persistent-hint
                                        @blur="fromDate = parseDate(fromDateFormatted)" v-on="on" :error-messages="fromDateError"></v-text-field>
                                </template>
                                <v-date-picker v-model="fromDate" no-title @input="fromDateMenu = false"></v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-menu ref="toDateMenu" v-model="toDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                <template v-slot:activator="{ on }">
                                    <v-text-field v-model="toDateFormatted" label="To Date" outlined dense hint="MM/DD/YYYY - Leave it blank if it is indefinitely" persistent-hint 
                                        @blur="toDate = parseDate(toDateFormatted)" v-on="on" :error-messages="toDateError" v-mask="'##/##/####'"></v-text-field>
                                </template>
                                <v-date-picker v-model="toDate" no-title @input="toDateMenu = false"></v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="6" md="3">
                            <v-text-field v-model="nightRate" label="Night Rate" outlined dense prefix="$" :error-messages="nightRateError"></v-text-field>
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model="tax" label="Tax" outlined dense suffix="%" :error-messages="taxError"></v-text-field>
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model="parkingFee" label="Parkin Fee" outlined dense prefix="$" :error-messages="parkingFeeError"></v-text-field>
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model="serviceFee" label="Service Fee" outlined dense suffix="%" :error-messages="serviceFeeError"></v-text-field>
                        </v-col>
                    </v-row>

                    <div class="subtitle-1">
                        Daily total: <strong>{{ formatMoney( dailyTotal ) }}</strong>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text depressed @click="rateDialog = false">Cancel</v-btn>
                    <v-btn depressed color="primary" @click="saveRate">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="pointsDialog" persistent scrollable width="900">
            <v-card>
                <v-card-title>
                    {{ selectedCustomer == null ? '' : selectedCustomer.fullName }}
                    <v-spacer></v-spacer>
                    <v-btn depressed color="primary" @click="usePointsDialog = true">
                        <v-icon left>mdi-plus</v-icon>
                        Use Points
                    </v-btn>
                </v-card-title>
                <v-card-subtitle>Points Statement</v-card-subtitle>
                <v-card-text>
                    <v-skeleton-loader v-model="loadingPoints" type="article">
                        <v-row>
                            <v-col cols="4">
                                <v-card color="blue lighten-4">
                                    <div class="headline text-center pt-2">{{ this.formatPoints( totalPointsAcumulated ) }}</div>
                                    <v-card-text class="text-center pa-2">
                                        Total acumulated
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <v-col cols="4">
                                <v-card color="orange lighten-4">
                                    <div class="headline text-center pt-2">{{ formatPoints( totalPointsUsed ) }}</div>
                                    <v-card-text class="text-center pa-2">
                                        Total used
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <v-col cols="4">
                                <v-card color="green lighten-4">
                                    <div class="headline text-center pt-2">{{ formatPoints( totalPointsAvailable ) }}</div>
                                    <v-card-text class="text-center pa-2">
                                        Total available
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-data-table :headers="pointsHeaders" :items="pointsInfo" :loading="loading" :items-per-page="99">
                            <template v-slot:item.total="{ item }">{{ formatMoney( item.total )}}</template>
                            <template v-slot:item.points="{ item }">{{ formatPoints( item.points )}}</template>
                        </v-data-table>
                    </v-skeleton-loader>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn depressed color="primary" @click="pointsDialog = false">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="usePointsDialog" persistent width="600">
            <v-card>
                <v-card-title>
                    Register Used Points
                    
                    <v-spacer></v-spacer>
                    <v-btn icon @click="usePointsDialog = false"><v-icon>mdi-close</v-icon></v-btn>    
                </v-card-title>
                <v-card-subtitle>
                    Register poins that the customer has used
                </v-card-subtitle>
                <v-card-text>

                    <v-row>
                        <v-col cols="6">
                            <v-text-field v-model="pointsUsed" outlined label="Points used" :error-messages="pointsUsedError" :hint="formatPoints( totalPointsAvailable ) + ' points available'" v-mask="['#','##','###','####','#####','######']" persistent-hint></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-menu ref="menu" v-model="pickerMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="pointsDate" outlined label="Use date" readonly v-bind="attrs" v-on="on" :error-messages="pointsDateError"></v-text-field>
                                </template>
                                <v-date-picker v-model="pointsDate" no-title scrollable @input="pickerMenu = false"></v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                    
                    <v-textarea v-model="pointsDescription" outlined label="Description" :error-messages="pointsDescriptionError"></v-textarea>
                    
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text depressed @click="usePointsDialog = false">Cancel</v-btn>
                    <v-btn depressed color="primary" @click="registerPoints" :loading="loadingPoints">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {mask} from 'vue-the-mask'
import moment from 'moment'
export default {
    data() {
        return {
            headers: [
                { text: 'Name', value: 'fullName', align: 'left', sortable: true },
                { text: 'Phone', value: 'phone', align: 'start', sortable: true },
                { text: 'Email', value: 'email', align: 'start', sortable: true },
                { text: 'Frequency', value: 'frequency', align: 'start', sortable: true },
                { text: 'Last Invoice Date', value: 'lastInvoiceDate', align: 'start', sortable: true },
                { text: 'Actions', value: 'actions', align: 'start', sortable: true },
            ], 
            customers: [],
            filter: '',

            pointsHeaders: [
                { text: 'Invoice #', value: 'id', align: 'start', sortable: true },
                { text: 'Total', value: 'total', align: 'right', sortable: true },
                { text: 'Points Acumulated', value: 'points', align: 'right', sortable: true },
            ],
            pointsInfo: [],

            customerDialog: false,
            selectedCustomer: null,
            pointsDialog: false,
            usePointsDialog: false,

            firstName: '',
            firstNameError: '',
            lastName: '',
            lastNameError: '',
            phone: '',
            phoneError: '',
            email: '',
            emailError: '',
            address1: '',
            address1Error: '',
            address2: '',
            address2Error: '',
            city: '',
            cityError: '',
            state: '',
            stateError: '',
            zipcode: '',
            zipcodeError: '',
            cardType: '',
            cardTypeError: '',
            cardLast4: '',
            cardLast4Error: '',
            frequencyFrom: '',
            frequencyFromError: '',
            frequency: '',
            frequencyError: '',
            nextDay: false,
            rates: [],
            ratesError: '',

            rateDialog: false,

            rateId: 0,
            fromDateMenu: false,
            toDateMenu: false,
            fromDate: new Date().toISOString().substr(0, 10),
            toDate: '',
            fromDateError: '',
            toDateError: '',
            fromDateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
            toDateFormatted: '',
            nightRate: '',
            nightRateError: '',
            tax: '',
            taxError: '',
            parkingFee: '',
            parkingFeeError: '',
            serviceFee: '',
            serviceFeeError: '',

            pointsUsed: '',
            pointsUsedError: '',
            pointsDate: new Date().toISOString().substr(0, 10),
            pointsDateError: '',
            pointsDescription: '',
            pointsDescriptionError: '',
            pickerMenu: false,

            rateAlert: false,
            rateAlertColor: '',
            rateAlertMessage: '',

            loading: false,
            saving: false,
            loadingPoints: false,
        }
    },
    computed: {
        dialogHeader() {
            return this.selectedCustomer == null ? 'Create Customer' : 'Edit Customer';
        },
        dailyTotal() {
            if( !this.isRateValid( false ) ) 
              return 0;

            let nRate = parseFloat( this.nightRate );
            let tx = parseFloat( this.tax );
            let pFee = parseFloat( this.parkingFee );
            let sFee = parseFloat( this.serviceFee );

            return nRate + ( tx / 100  * nRate ) + pFee + ( sFee / 100 * nRate );
        },
        totalPointsAcumulated() {
            let total = 0;
            this.pointsInfo.forEach(p => {
                total += parseInt( p.points );
            });

            return total;
        },
        totalPointsUsed() {
            let total = 0;
            if( this.pointsInfo.length > 0 )
                total = this.pointsInfo[0].usedPoints;

            return total;
        },
        totalPointsAvailable() {
            return this.totalPointsAcumulated - this.totalPointsUsed;
        },
    },
    watch: {
        fromDate( v ) {
            this.fromDateFormatted = this.formatDate( v );
        },
        toDate( v ) {
            this.toDateFormatted = this.formatDate( v );
        },
    },
    mounted() {
        document.title = this.$route.name;
        this.init();
    },
    methods: {
        init() {
            this.loading = true;
            this.$http
                .get( '/proc/data.php', {
                    params: {
                        action: 'getCustomers'
                    }
                })
                .then( response => { 
                    if( response.data.message == 'Unauthorized' ) 
                        this.$router.push('/login');

                    this.loading = false;
                    this.customers = response.data;
                });
        },
        openEditDialog( customer ) {
            if( customer == null ) {
                customer = {
                    id: 0,
                    firstName: '',
                    lastName: '',
                    phone: '',
                    email: '',
                    address1: '',
                    address2: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    cardType: '',
                    cardLast4: '',
                    frequency: '',
                    frequencyFrom: '',
                    nextDay: false,
                    rates:[],
                };
            }
            else
                this.selectedCustomer = customer;

            this.clearDialog();
            
            this.firstName = customer.firstName;
            this.lastName = customer.lastName;
            this.phone = customer.phone;
            this.email = customer.email;
            this.address1 = customer.address1;
            this.address2 = customer.address2;
            this.city = customer.city;
            this.state = customer.state;
            this.zipcode = customer.zipcode;
            this.cardType = customer.cardType;
            this.cardLast4 = customer.cardLast4;
            this.frequency = customer.frequency;
            this.frequencyFrom = customer.frequencyFrom;
            this.nextDay = customer.nextDay;
            this.rates = customer.rates;
            this.nextDay = customer.nextDay == 1 ? true : false;

            this.customerDialog = true;
        },
        openPointsDialog( customer ) {
            if( customer == null ) {
                return;
            }
            else 
                this.selectedCustomer = customer;
            
            this.pointsDialog = true;

            // get points statement of the customer
            this.loadingPoints = true;
            this.$http
                .get( '/proc/data.php', {
                    params: {
                        action: 'getCustomerPoints',
                        id: customer.id
                    }
                })
                .then( response => { 
                    if( response.data.message == 'Unauthorized' ) 
                        this.$router.push('/login');

                    this.loadingPoints = false;
                    this.pointsInfo = response.data;
                });
        },
        clearDialog() {
            this.firstName = this.lastName = this.phone = this.email = this.address1 = this.address2 = this.city = this.state = this.zipcode = this.cardType = this.cardLast4 = this.frequency = this.frequencyFrom =
            this.firstNameError = this.lastNameError = this.phoneError = this.emailError = this.address1Error = this.address2Error = this.cityError = this.stateError = this.zipcodeError = 
            this.cardTypeError = this.cardLast4Error = this.frequencyError = this.frequencyFromError = this.ratesError = '';
            this.nextDay = false;
            this.rates = [];
        },
        isValid() {
            let errorFound = false;
            this.firstNameError = this.lastNameError = this.phoneError = this.emailError = this.address1Error = this.cityError = this.stateError = this.zipcodeError = this.ratesError = '';

            if( this.firstName == undefined || this.firstName == null || this.firstName.length == 0 ) {
                this.firstNameError = 'required';
                errorFound = true;
            }
            if( this.lastName == undefined || this.lastName == null || this.lastName.length == 0 ) {
                this.lastNameError = 'required';
                errorFound = true;
            }
            // if( this.phone == undefined || this.phone == null || this.phone.length != 14 ) {
            //     this.phoneError = 'required';
            //     errorFound = true;
            // }
            if( this.email == undefined || this.email == null || !/.{2,30}@.{2,30}\..{2,4}/g.test( this.email ) ) {
                this.emailError = 'required';
                errorFound = true;
            }
            if( this.address1 == undefined || this.address1 == null || this.address1.length == 0 ) {
                this.address1Error = 'required';
                errorFound = true;
            }
            if( this.city == undefined || this.city == null || this.city.length == 0 ) {
                this.cityError = 'required';
                errorFound = true;
            }
            if( this.state == undefined || this.state == null || this.state.length != 2 ) {
                this.stateError = 'required';
                errorFound = true;
            }
            if( this.zipcode == undefined || this.zipcode == null || this.zipcode.length < 5 ) {
                this.zipcodeError = 'required';
                errorFound = true;
            }
            if( this.rates.length == 0 ) {
                this.ratesError = 'required';
                errorFound = true;
            }

            return !errorFound;
        },
        saveCustomer() {
            if( !this.isValid() )
                return;
            
            let customer =  {
                id:                 this.selectedCustomer == null ? 0 : this.selectedCustomer.id,
                firstname:          this.firstName,
                lastname:           this.lastName,
                address1:           this.address1,
                address2:           this.address2,
                city:               this.city,
                state:              this.state,
                zipcode:            this.zipcode,
                phone:              this.phone,
                email:              this.email,
                frequency:          this.frequency,
                frequencyFrom:      this.frequencyFrom,
                cardType:           this.cardType,
                cardlast4:          this.cardLast4,
                nextDay:            this.nextDay == true ? 1 : 0,
                rates:              this.rates
            };

            let axiosOptions = {
                url: '/proc/data.php',
                method: 'post',
                data: {
                    customer: customer
                },
                params: {
                    action: 'saveCustomer'
                },
                json: true,
            };

            this.saving = true;
            this.$http( axiosOptions )
                .then( response => {
                    if( response.data.message == 'Unauthorized' ) 
                        this.$router.push('/login');
                        
                    if( response.data.status == true ) {
                        this.customerDialog = false;
                        this.saving = false;
                        this.loading = true;
                        this.$http
                            .get( '/proc/data.php', {
                                params: {
                                    action: 'getCustomers'
                                }
                            })
                            .then( response => { 
                                this.loading = false;
                                this.customers = response.data;
                            });
                    }
                });
        },
        formatDate( date ) {
            if( !date ) return null;

            const [year, month, day] = date.split('-');
            return `${month}/${day}/${year}`
        },
        parseDate ( date ) {
            if( !date ) return null;

            const [month, day, year] = date.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        },
        clearRateForm() {
            this.rateId = 0;
            this.fromDate = new Date().toISOString().substr(0, 10);
            this.toDate = '';
            this.nightRate = '';
            this.tax = '';
            this.parkingFee = '';
            this.serviceFee = '';
            this.rateAlert = false;

            this.fromDateError = this.toDateError = this.nightRateError = this.taxError = this.parkingFeeError = this.serviceFeeError = this.rateAlertMessage = '';
        },
        openRateDialog( rate ) {
            if( rate == undefined || rate == null ) {
                this.clearRateForm();
                this.rateDialog = true;
            }
            else {
                this.clearRateForm();
                this.rateId = rate.id;
                this.fromDate = rate.from_date;
                this.toDate = rate.to_date;
                this.nightRate = rate.night_rate;
                this.tax = rate.tax;
                this.parkingFee = rate.parking_fee;
                this.serviceFee = rate.service_fee;

                this.rateDialog = true;
            }
        },
        isRateValid( showMessages = true ) {
            let errorFound = false;
            if( showMessages ) {
                this.rateAlert = false;
                this.fromDateError = this.toDateError = this.nightRateError = this.taxError = this.parkingFeeError = this.serviceFeeError = this.rateAlertMessage = '';
            }

            if( this.fromDate != undefined && this.fromDate != null && this.fromDate.length > 0 && !moment( this.fromDate ).isValid() ) {
                errorFound = true;
                if( showMessages) 
                    this.fromDateError = 'invalid date';
            }
            if( this.toDate != undefined && this.toDate != null && this.toDate.length > 0 && !moment( this.toDate ).isValid() ) {
                errorFound = true;
                if( showMessages) 
                    this.toDateError = 'must be a date';
            }
            else if( this.toDate != undefined && this.toDate != null && this.toDate.length > 0 && moment( this.toDate ).isBefore( moment( this.fromDate ) ) ) {
                errorFound = true;
                if( showMessages) 
                    this.toDateError = 'can\'t be before From date';
            }
            if( this.nightRate == undefined || this.nightRate == null || this.nightRate.length == 0|| isNaN( parseFloat( this.nightRate ) ) || parseFloat( this.nightRate ) <= 0  ) {
                errorFound = true;
                if( showMessages) 
                    this.nightRateError = 'required';
            }
            if( this.tax == undefined || this.tax == null || this.tax.length == 0 || isNaN( parseFloat( this.tax ) ) || parseFloat( this.tax ) < 0 ) {
                errorFound = true;
                if( showMessages) 
                    this.taxError = 'required';
            }
            if( this.parkingFee == undefined || this.parkingFee == null || this.parkingFee.length == 0 || isNaN( parseFloat( this.parkingFee ) ) || parseFloat( this.parkingFee ) < 0 ) {
                errorFound = true;
                if( showMessages) 
                    this.parkingFeeError = 'required';
            }
            if( this.serviceFee == undefined || this.serviceFee == null || this.serviceFee.length == 0 || isNaN( parseFloat( this.serviceFee ) ) || parseFloat( this.serviceFee ) < 0 ) {
                errorFound = true;
                if( showMessages) 
                    this.serviceFeeError = 'required';
            }

            return !errorFound;
        },
        saveRate() {
            if( !this.isRateValid() ) 
                return;

            let newRateFromDate = moment( this.fromDate );
            let newRateToDate = moment( this.toDate == null || this.toDate.length == 0 ? '9999-12-31' : this.toDate );
            
            // check if there are overlapping ranges
            let overlapping = false;
            this.rates.forEach(r => {
                let from = moment( r.from_date );
                let to = moment( r.to_date == null || r.to_date == 0 ? '9999-12-31' : r.to_date );

                if( this.rateId != r.id ) {
                    if( newRateFromDate.isBetween( from, to ) || newRateToDate.isBetween( from, to ) ||
                        newRateFromDate.isSame( from ) || newRateFromDate.isSame( to ) ||
                        newRateToDate.isSame( from ) || newRateToDate.isSame( to ) ) {
                        this.rateAlertColor = 'red';
                        this.rateAlertMessage = 'This date range overlap a previously defined range';
                        this.rateAlert = true;
                        overlapping = true;
                        return;
                    }
                }
            });

            if( overlapping )
                return;

            if( this.rateId == 0 ) {
                this.rates.push({
                    id:             'tmp' + this.rates.length,
                    from_date:      this.fromDate,
                    to_date:        this.toDate,
                    night_rate:     this.nightRate,
                    tax:            this.tax,
                    parking_fee:    this.parkingFee,
                    service_fee:    this.serviceFee,
                    daily_total:     this.dailyTotal
                });
            }
            else {
                let currentRate = this.rates.find( r => {
                    return r.id == this.rateId;
                });

                if( currentRate != undefined && currentRate != null ) {
                    currentRate.from_date = this.fromDate;
                    currentRate.to_date = this.toDate;
                    currentRate.night_rate = this.nightRate;
                    currentRate.tax = this.tax;
                    currentRate.parking_fee = this.parkingFee;
                    currentRate.service_fee = this.serviceFee;
                    currentRate.daily_total = this.dailyTotal;
                }
            }

            this.rateDialog = false;
        },
        isPointsFormValid() {
            let errorFound = false;

            if( this.pointsUsed == undefined || this.pointsUsed == null || this.pointsUsed.length == 0 ) {
                errorFound = true;
                this.pointsUsedError = 'required';
            }
            if( this.pointsDate == undefined || this.pointsDate == null || this.pointsDate.length == 0 ) {
                errorFound = true;
                this.pointsDateError = 'required';
            }
            else if( !moment(this.pointsDate).isValid() ) {
                errorFound = true;
                this.pointsDateError = 'must be valid';
            }

            return !errorFound;
        },
        registerPoints() {
            if( !this.isPointsFormValid() ) 
                return;

            let axiosOptions = {
                url: '/proc/data.php',
                method: 'post',
                data: {
                    customerId      : 91, // customer.id
                    pointsUsed      : this.pointsUsed,
                    date            : this.pointsDate,
                    description     : this.pointsDescription
                },
                params: {
                    action: 'saveCustomerPoints'
                },
                json: true,
            };

            this.loadingPoints = false;
            this.$http( axiosOptions )
                .then( response => {
                    if( response.data.message == 'Unauthorized' ) 
                        this.$router.push('/login');
                        
                    if( response.data.status == true ) {
                        this.usePointsDialog = false;
                        this.loadingPoints = false;
                        this.loading = true;
                        this.$http
                            .get( '/proc/data.php', {
                                params: {
                                    action: 'getCustomers'
                                }
                            })
                            .then( response => { 
                                this.loading = false;
                                this.customers = response.data;
                            });
                        
                        this.pointsInfo[0].usedPoints = parseInt( this.pointsInfo[0].usedPoints ) +  parseInt( this.pointsUsed );
                    }
                });
        },
        formatMoney: function(n, c, d, t) {
            c = isNaN(c = Math.abs(c)) ? 2 : c;
            d = d == undefined ? "." : d;
            t = t == undefined ? "," : t;
            let s = n < 0 ? "-" : "";
            let i = String(parseInt(n = Math.abs(Number(n) || 0).toFixed(c)));
            let j = (j = i.length) > 3 ? j % 3 : 0;
            
            return '$' + s + (j ? i.substr(0, j) + t : "") + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + t) + (c ? d + Math.abs(n - i).toFixed(c).slice(2) : "");
        },
        formatPoints: function(n) {
            let c = 0;
            let d = d == undefined ? "." : d;
            let t = t == undefined ? "," : t;
            let s = n < 0 ? "-" : "";
            let i = String(parseInt(n = Math.abs(Number(n) || 0).toFixed(c)));
            let j = (j = i.length) > 3 ? j % 3 : 0;
            
            return s + (j ? i.substr(0, j) + t : "") + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + t) + (c ? d + Math.abs(n - i).toFixed(c).slice(2) : "");
        },
    },
    directives: { 
        mask
    }
}
</script>

<style scoped>

</style>